// src/components/common/LanguageSelector/LanguageSelector.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import 'flag-icons';
import styles from './LanguageSelector.module.css';

const LanguageSelector = ({ variant = 'header' }) => {
  const { currentLanguage, languages, changeLanguage, t } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setIsOpen(false);
  };

  const currentLang = languages.find(lang => lang.code === currentLanguage);

  // Determine wrapper class for auth variant
  const getWrapperClass = () => {
    if (variant === 'auth') {
      return styles.authLanguageSelector;
    }
    return '';
  };

  // Determine selector class for header variant
  const getSelectorClass = () => {
    const baseClass = styles.languageSelector;
    if (variant === 'header') {
      return `${baseClass} ${styles.header}`;
    }
    return baseClass;
  };

  return (
    <div className={getWrapperClass()}>
      <div className={getSelectorClass()} ref={dropdownRef}>
        <button
          className={styles.languageButton}
          onClick={() => setIsOpen(!isOpen)}
          title={t('language.selectLanguage')}
        >
          <Globe size={18} className={styles.languageIcon} />
          <span className={styles.languageText}>{currentLang?.nativeName}</span>
          <ChevronDown 
            size={14} 
            className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} 
          />
        </button>

        {isOpen && (
          <div className={styles.languageDropdown}>
            <div className={styles.languageDropdownHeader}>
              <h4>{t('language.selectLanguage')}</h4>
            </div>
            <div className={styles.languageList}>
              {languages.map((language) => (
                <button
                  key={language.code}
                  className={`${styles.languageOption} ${
                    currentLanguage === language.code ? styles.active : ''
                  }`}
                  onClick={() => handleLanguageChange(language.code)}
                >
                  <div className={styles.languageInfo}>
                    <span className={`fi fi-${language.countryCode?.toLowerCase() || language.flag} ${styles.languageFlagLarge}`}></span>
                    <div className={styles.languageNames}>
                      <span className={styles.languageNative}>{language.nativeName}</span>
                      <span className={styles.languageEnglish}>{language.name}</span>
                    </div>
                  </div>
                  {currentLanguage === language.code && (
                    <Check size={16} className={styles.checkIcon} />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;